/**
 * BudgetPage — 50/30/20 breakdown and category mapper
 *
 * Two modes:
 *   - overview: Month selector, 3 BudgetGauge components, budget summary,
 *     category breakdown grouped by bucket (Needs/Wants/Savings)
 *   - mapper: List of all categories with inline bucket select to reassign
 *
 * Phase 5: Full implementation wired to Firestore
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserConfig, getTransactionsByMonth, getCategoryOverrides,
  saveCategoryOverride,
} from '../services/db';
import { calculateBudget, BUCKETS, BUCKET_META, getCategoryBucket } from '../utils/budget';
import { useCategoryContext } from '../contexts/CategoryContext';
import { formatCurrency, getMonthKey, shiftMonthKey, formatMonthLabel } from '../utils/format';
import BudgetGauge from '../components/BudgetGauge';
import { SkeletonLine } from '../components/Skeleton';
import theme from '../utils/theme';

export default function BudgetPage() {
  const { user } = useAuth();
  const { bucketNames } = useCategoryContext();
  const [monthKey, setMonthKey] = useState(getMonthKey());
  const [transactions, setTransactions] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [income, setIncome] = useState(8030);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('overview'); // overview | mapper
  const [saveMsg, setSaveMsg] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Load transactions, overrides, and user config for selected month.
   */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [config, txns, cats] = await Promise.all([
        getUserConfig(user.id),
        getTransactionsByMonth(user.id, monthKey),
        getCategoryOverrides(user.id),
      ]);
      setIncome(config?.conservative_income || 8030);
      setTransactions(txns);
      setOverrides(cats);
    } catch (err) {
      console.error('Failed to load budget data:', err);
      setError(err.message || 'Failed to load budget data');
    } finally {
      setLoading(false);
    }
  }, [user.id, monthKey]);

  useEffect(() => { loadData(); }, [loadData]);

  /**
   * Aggregate transactions by category, respecting overrides.
   * Returns { categoryMap, needTotal, wantTotal, savingsTotal, incomeTotal }
   */
  const aggregated = useMemo(() => {
    const categoryMap = {}; // { [category]: { amount, count, bucket } }
    let needTotal = 0, wantTotal = 0, savingsTotal = 0, incomeTotal = 0;

    for (const txn of transactions) {
      const cat = txn.category || 'Uncategorized';
      const bucket = txn.bucket || getCategoryBucket(cat, overrides);

      if (bucket === 'income' || txn.is_credit) {
        incomeTotal += txn.amount || 0;
        continue;
      }

      if (!categoryMap[cat]) {
        categoryMap[cat] = { amount: 0, count: 0, bucket };
      }
      categoryMap[cat].amount += txn.amount || 0;
      categoryMap[cat].count += 1;
      // Update bucket in case override changed
      categoryMap[cat].bucket = bucket;

      if (bucket === 'need') needTotal += txn.amount || 0;
      else if (bucket === 'savings') savingsTotal += txn.amount || 0;
      else wantTotal += txn.amount || 0;
    }

    return { categoryMap, needTotal, wantTotal, savingsTotal, incomeTotal };
  }, [transactions, overrides]);

  const budgetData = useMemo(() => calculateBudget({
    income,
    needActual: aggregated.needTotal,
    wantActual: aggregated.wantTotal,
    savingsActual: aggregated.savingsTotal,
  }), [income, aggregated]);

  /**
   * Handle bucket override change from category mapper.
   */
  const handleBucketChange = async (category, newBucket) => {
    // Update local state immediately
    setOverrides(prev => ({ ...prev, [category]: newBucket }));

    try {
      await saveCategoryOverride(user.id, category, newBucket);
      setSaveMsg(`${category} → ${BUCKET_META[newBucket]?.label || newBucket}`);
      setTimeout(() => setSaveMsg(null), 2000);
    } catch (err) {
      console.error('Failed to save override:', err);
    }
  };

  /**
   * Group categories by bucket for the overview breakdown.
   */
  const categoryGroups = useMemo(() => {
    const groups = { need: [], want: [], savings: [] };
    for (const [cat, data] of Object.entries(aggregated.categoryMap)) {
      const bucket = data.bucket;
      if (groups[bucket]) {
        groups[bucket].push({ name: cat, ...data });
      } else {
        groups.want.push({ name: cat, ...data });
      }
    }
    // Sort each group by amount descending
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => b.amount - a.amount);
    }
    return groups;
  }, [aggregated]);

  if (loading) {
    return (
      <div>
        <div className="month-selector">
          <button onClick={() => setMonthKey(k => shiftMonthKey(k, -1))}>&#8249;</button>
          <span className="month-label">{formatMonthLabel(monthKey)}</span>
          <button onClick={() => setMonthKey(k => shiftMonthKey(k, 1))}>&#8250;</button>
        </div>
        <div className="skeleton-gauges-row">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-gauge-card">
              <SkeletonLine className="skeleton-line--tall" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </div>
        <div className="skeleton-card">
          <SkeletonLine className="skeleton-line--medium" />
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton-row">
              <SkeletonLine className="skeleton-line--medium" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </div>
        <div className="skeleton-card">
          <SkeletonLine className="skeleton-line--medium" />
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-row">
              <SkeletonLine className="skeleton-line--medium" />
              <SkeletonLine className="skeleton-line--short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => { setError(null); setLoading(true); loadData(); }}>
          Retry
        </button>
      </div>
    );
  }

  // ==================== MAPPER MODE ====================
  if (mode === 'mapper') {
    const allCategories = Object.entries(aggregated.categoryMap)
      .sort((a, b) => b[1].amount - a[1].amount);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => setMode('overview')}>
            ← Back
          </button>
          <h2 style={{ fontSize: '1rem', fontWeight: 700 }}>Edit Categories</h2>
        </div>

        {saveMsg && <div className="alert alert-success">{saveMsg}</div>}

        <div className="card">
          {allCategories.length === 0 ? (
            <p className="empty-state">No categories to edit this month.</p>
          ) : (
            allCategories.map(([cat, data]) => (
              <div key={cat} className="category-row">
                <span className="category-name">{cat}</span>
                <span className="category-amount">{formatCurrency(data.amount)}</span>
                <select
                  className="category-select"
                  value={data.bucket}
                  onChange={e => handleBucketChange(cat, e.target.value)}
                >
                  <option value="need">{bucketNames.need}</option>
                  <option value="want">{bucketNames.want}</option>
                  <option value="savings">{bucketNames.savings}</option>
                </select>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ==================== OVERVIEW MODE ====================
  const totalSpent = aggregated.needTotal + aggregated.wantTotal;
  const remaining = income - totalSpent;

  return (
    <div>
      {/* Month selector */}
      <div className="month-selector">
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, -1))}>‹</button>
        <span className="month-label">{formatMonthLabel(monthKey)}</span>
        <button onClick={() => setMonthKey(k => shiftMonthKey(k, 1))}>›</button>
      </div>

      {/* Budget gauges */}
      <div className="gauges-row">
        <BudgetGauge
          label={bucketNames.need} emoji={BUCKET_META[BUCKETS.NEED].emoji} bucket="need"
          percent={budgetData.buckets[BUCKETS.NEED].percent} target="50%"
          actual={budgetData.buckets[BUCKETS.NEED].actual}
          budget={budgetData.buckets[BUCKETS.NEED].target}
          status={budgetData.buckets[BUCKETS.NEED].status}
        />
        <BudgetGauge
          label={bucketNames.want} emoji={BUCKET_META[BUCKETS.WANT].emoji} bucket="want"
          percent={budgetData.buckets[BUCKETS.WANT].percent} target="30%"
          actual={budgetData.buckets[BUCKETS.WANT].actual}
          budget={budgetData.buckets[BUCKETS.WANT].target}
          status={budgetData.buckets[BUCKETS.WANT].status}
        />
        <BudgetGauge
          label={bucketNames.savings} emoji={BUCKET_META[BUCKETS.SAVINGS].emoji} bucket="savings"
          percent={budgetData.buckets[BUCKETS.SAVINGS].percent} target="20%"
          actual={budgetData.buckets[BUCKETS.SAVINGS].actual}
          budget={budgetData.buckets[BUCKETS.SAVINGS].target}
          status={budgetData.buckets[BUCKETS.SAVINGS].status}
        />
      </div>

      {/* Budget summary */}
      <div className="card">
        <h2>Summary</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
          <span>Income (budget)</span>
          <span style={{ fontWeight: 600 }}>{formatCurrency(income)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
          <span>Total spent</span>
          <span style={{ fontWeight: 600 }}>{formatCurrency(totalSpent)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', paddingTop: '0.35rem', borderTop: `1px solid ${theme.borderLight}` }}>
          <span style={{ fontWeight: 600 }}>Remaining</span>
          <span style={{ fontWeight: 700, color: remaining >= 0 ? theme.success : theme.danger }}>
            {formatCurrency(remaining)}
          </span>
        </div>
      </div>

      {/* Category breakdown by bucket */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h2 style={{ marginBottom: 0 }}>Categories</h2>
          <button className="btn btn-sm btn-secondary" onClick={() => setMode('mapper')}>
            Edit Categories
          </button>
        </div>

        {[BUCKETS.NEED, BUCKETS.WANT, BUCKETS.SAVINGS].map(bucket => {
          const meta = BUCKET_META[bucket];
          const cats = categoryGroups[bucket];
          const bucketTotal = cats.reduce((s, c) => s + c.amount, 0);
          if (cats.length === 0) return null;

          return (
            <div key={bucket} className="bucket-section">
              <div className="bucket-header">
                <span className="bucket-header-label" style={{ color: meta.color }}>
                  {meta.emoji} {bucketNames[bucket] || meta.label}
                </span>
                <span className="bucket-header-amount">{formatCurrency(bucketTotal)}</span>
              </div>
              {cats.map(cat => (
                <div key={cat.name} className="category-row">
                  <span className="category-name">{cat.name}</span>
                  <span className="category-amount">{formatCurrency(cat.amount)}</span>
                </div>
              ))}
            </div>
          );
        })}

        {Object.keys(aggregated.categoryMap).length === 0 && (
          <p className="empty-state">No spending data for this month.</p>
        )}
      </div>
    </div>
  );
}
